import { useCallback, useEffect, useRef, useState } from 'react';
import { C } from '@ridgewill/brand';
import { LOGIN_URL, authClient, useSession } from './lib/auth-client.js';
import { MAX_ACCOUNTS, chooserUrl, listDeviceAccounts, switchTo, type DeviceAccount } from './lib/device-accounts.js';
import {
  allowBounce,
  shouldBounce,
  suppressBounce,
  markSignedOut,
  signedOutDeliberately,
  clearSignedOut,
} from './lib/bounce.js';
import { Profile } from './screens/Profile.js';
import { Security } from './screens/Security.js';
import { skyImageAt } from './lib/sky.js';
import { SITE_URL } from './lib/site.js';
import { Agreements } from './screens/Agreements.js';
import { Subscriptions } from './screens/Subscriptions.js';
import { Connections } from './screens/Connections.js';
import { ConnectionDetail } from './screens/ConnectionDetail.js';
import { Support, SupportTicket } from './screens/Support.js';
import { referenceOf } from './lib/support.js';
import { connectionIdOf } from './lib/connections.js';
import { RecoveryReviewPrompt } from './components/RecoveryReviewPrompt.js';
import { ThemeControl } from './components/ThemeControl.js';
import { EmailNotVerifiedBanner } from './components/EmailVerification.js';
import { EmailVerifiedContext, isUnverified } from './lib/email-verification.js';
import { AgreementGate } from './components/AgreementGate.js';
import { gatesAccount, getFamily, getPendingAgreements, type Agreement } from './lib/api.js';
import { Family } from './screens/Family.js';
import { FamilyJoin } from './screens/FamilyJoin.js';
import { Dependent } from './screens/Dependent.js';
import { FAMILY_PATH, JOIN_PATH, dependentIdOf } from './lib/family.js';

/**
 * Routing by pathname, with no router dependency — the same choice as
 * `apps/login` in the auth repo, and for the same reason: these are full page
 * loads and a client-side router would manage transitions that do not happen.
 *
 * It earns its place the first time a screen has to change what it shows WITHOUT
 * navigating. Two-factor enrolment is that shape and holds the state internally
 * instead, which is why it is still not needed here.
 */
const TABS = [
  { path: '/', label: 'Profile' },
  /**
   * Security holds passwords, two-factor, passkeys AND devices.
   *
   * They were three tabs. One subject — how you get in, and who currently can —
   * so one screen with sections. Separate tabs described the implementation (a
   * credential type per plugin) rather than the question somebody arrives with,
   * and they hid the fact that changing a password was not possible at all.
   */
  { path: '/security', label: 'Security' },
  /**
   * The people you are connected with, and the people you have blocked, across
   * every app (RidgeWill/account#79, adr/0012).
   *
   * A TAB for the same reason Subscriptions is one: its subject is a
   * relationship rather than this account's settings, and products link to it,
   * so it needs a URL of its own (`/connections`, `/connections#blocked`).
   */
  { path: '/connections', label: 'Connections' },
  /**
   * Family Group (account#117): present only once the person is in a group.
   * Everyone else reaches Family Groups from the row on Connections, which is
   * shown only to accounts that may start one (21 or older). Beside
   * Connections, since a group is people you are connected with (Jon, 2026-10-15).
   */
  { path: FAMILY_PATH, label: 'Family Group', familyOnly: true },
  /**
   * What you pay Ridgewill for, across every app (auth#435).
   *
   * A TAB, not a section of Profile. It is the one screen in the portal whose
   * subject is not this account's own settings but its relationship with the
   * products, and adr/0016 rests on it being reachable rather than filed
   * somewhere a person has to already know about. It also has to be linkable:
   * "check your subscriptions" in a billing email needs a URL.
   */
  { path: '/subscriptions', label: 'Subscriptions' },
  { path: '/agreements', label: 'Agreements' },
  /**
   * "Contact support" (Jon, 2026-09-27). Last, because it is where somebody
   * goes when something else did not work, not a place anybody browses.
   */
  { path: '/support', label: 'Support' },
] as const;

export function App() {
  const { data: session, isPending, error, refetch } = useSession();
  const state = sessionState({ isPending, error, session });
  const owed = useOwedAgreements(
    state === 'ready' ? (session?.user as { pendingAgreements?: unknown } | undefined)?.pendingAgreements : [],
  );
  const inFamily = useInFamily(state === 'ready');

  if (state === 'loading') return <Shell title="Loading…">{null}</Shell>;

  if (state === 'signed-out') return <SignedOut />;

  /**
   * A FAILED session lookup is NOT a signed-out user, and conflating them is a
   * mistake this org has already paid for once (auth#32/#33): `get-session` was
   * returning 500 and the screen calmly reported a normal sign-out. The message
   * was plausible, confident and wrong, and it sent an hour of debugging at the
   * wrong layer.
   *
   * `fault` now covers a SECOND way to be unusable, and it is the quieter one:
   * a 200 whose body is not a session. See `sessionState` for why that lands
   * here rather than rendering a signed-in page with no account in it.
   */
  if (state === 'fault') {
    console.error('[account] unusable session', { error, session });
    return (
      <ServerFault
        detail={
          error
            ? (error instanceof Error && error.message) || undefined
            : 'The server answered, but sent back no account details.'
        }
      />
    );
  }

  /**
   * `ready` is the only state left, and that is what makes this cast true
   * rather than hopeful. It sat directly under a `!session` guard and asserted
   * a shape nothing had checked, which is #16: one absent `user` and the whole
   * portal rendered nothing at all.
   */
  const user = session?.user as {
    email: string;
    name?: string | null;
    username?: string | null;
    displayUsername?: string | null;
    image?: string | null;
    twoFactorEnabled?: boolean | null;
    /** Since RidgeWill/auth#587 an account can be signed in before this is true. */
    emailVerified?: boolean | null;
  };
  const unverified = isUnverified(user);
  const here = normalisePath(window.location.pathname);
  const connectionId = connectionIdOf(here);
  const ticketReference = referenceOf(here);
  const dependentId = dependentIdOf(here);

  /**
   * `refetch` after a mutation, rather than trusting local state.
   *
   * The session is the source for name, username, avatar and whether two-factor
   * is on. A screen that updated its own copy would drift from what every other
   * screen reads — and the header would still show the old name.
   */
  const changed = () => void refetch();

  /**
   * RIDGEWILL'S OWN AGREEMENTS COME FIRST (Jon, 2026-10-10).
   *
   * Until they are accepted the only screens are the gate and Support, which
   * stays reachable so somebody with a question about the new terms can ask
   * it. Profile, Security and the rest wait. See components/AgreementGate for
   * what is gated and why the ways out are what they are.
   *
   * While the check is in flight nothing renders, the same as the session
   * check above it: painting the requested screen and then replacing it with a
   * gate would show somebody their account and take it away again.
   */
  if (owed.status === 'checking') return <Shell title="Loading…">{null}</Shell>;
  const onSupport = here === '/support' || Boolean(ticketReference);
  /**
   * THE INVITATION PAGE PASSES THE GATE (account#117). Someone under 18 owes a
   * guardian's half of every agreement until a guardian accepts it, which
   * cannot happen until they have joined a Family Group from this page.
   */
  const onJoin = here === JOIN_PATH;
  if (owed.pending.length > 0 && !onSupport && !onJoin) {
    return (
      <Shell title="Before you continue" email={user.email} name={user.name} image={user.image}>
        <AgreementGate
          pending={owed.pending}
          onSignOut={() => void signOut()}
          onAccepted={() => {
            owed.recheck();
            changed();
          }}
        />
      </Shell>
    );
  }

  const screen =
    here === '/security' ? (
      <Security
        twoFactorEnabled={Boolean(user.twoFactorEnabled)}
        onChanged={changed}
        email={user.email}
        currentToken={(session?.session as { token?: string } | undefined)?.token}
      />
    ) : here === '/connections' ? (
      <Connections />
    ) : connectionId ? (
      <ConnectionDetail id={connectionId} />
    ) : here === '/subscriptions' ? (
      <Subscriptions />
    ) : here === '/agreements' ? (
      <Agreements />
    ) : here === FAMILY_PATH ? (
      <Family />
    ) : here === JOIN_PATH ? (
      <FamilyJoin />
    ) : dependentId ? (
      <Dependent userId={dependentId} />
    ) : here === '/support' ? (
      <Support />
    ) : ticketReference ? (
      <SupportTicket key={ticketReference} reference={ticketReference} />
    ) : (
      <Profile user={user} onChanged={changed} />
    );

  return (
    /*
      The whole tree, so every section that offers something an unverified
      account is refused can hide it and say why, instead of letting the server
      refuse (account#112). A context rather than a prop through every screen:
      five sections on two screens need one boolean, and threading it would
      touch every signature in between.
    */
    <EmailVerifiedContext.Provider value={!unverified}>
      <Shell
        title="Your account"
        /* No tabs while agreements are owed: on Support they would offer
           screens that only lead back to the gate. */
        nav={owed.pending.length === 0}
        family={inFamily === true}
        here={here}
        email={user.email}
        name={user.name}
        image={user.image}
        /*
          ON EVERY SCREEN, and outside the card (auth#383). The people whose
          recovery address has rotted are the ones who never open Security, so a
          prompt that waits there reaches nobody who needs it. Renders nothing at
          all unless a review is actually due.

          The unverified banner sits ABOVE it (account#112): it explains why
          things are missing on whatever screen is open, and it is the one with
          something to do first.
        */
        banner={
          <>
            {owed.pending.length > 0 ? <OwedBanner count={owed.pending.length} /> : null}
            {owed.waitingForGuardian.length > 0 ? <GuardianBanner agreements={owed.waitingForGuardian} inFamily={inFamily} /> : null}
            {unverified ? <EmailNotVerifiedBanner onChanged={changed} /> : null}
            <RecoveryReviewPrompt />
          </>
        }
      >
        <BounceReset />
        {screen}
      </Shell>
    </EmailVerifiedContext.Provider>
  );
}

/**
 * Sign out, from the header or from the agreement gate's ways out.
 *
 * With more than one account in this browser, "Sign out" goes to auth's account
 * chooser instead, which asks WHICH account and comes back here. `signOut()`
 * would end every one of them, and nothing in the words "Sign out" says so.
 * When the list cannot be had, this signs out as it always has.
 */
async function signOut() {
  const held = await listDeviceAccounts();
  if (held && held.length > 1) {
    window.location.assign(chooserUrl(returnQuery()));
    return;
  }
  /**
   * Suppress the redirect FIRST. Signing out lands on `/` with no
   * session, which is exactly the condition that sends somebody to
   * the sign-in page — so without this, pressing Sign out puts them
   * straight back into the sign-in form and undoes, instantly and
   * invisibly, the thing they just asked for.
   */
  suppressBounce();
  /**
   * And say WHY the session is about to be missing. `/` cannot
   * otherwise tell "I asked for this" from "a sign-in round trip
   * came back empty", and the two want opposite treatment - one
   * should be carried on to sign-in, the other must stop or it
   * loops. See lib/bounce.ts.
   */
  markSignedOut();
  void authClient.signOut().then(() => window.location.assign('/'));
}

/**
 * What Ridgewill's own agreements say this account still owes.
 *
 * ── THE SESSION FIELD DECIDES WHETHER TO ASK, NOT WHAT THE ANSWER IS ──
 *
 * `user.pendingAgreements` is a cache that can over-report (an app's terms,
 * a retired agreement) but is empty only when nothing is owed, so an empty
 * array skips the request entirely and almost every page load costs nothing.
 * Anything else, including the field being missing, asks the endpoint, and
 * the endpoint's answer is narrowed to what the gate covers: Ridgewill's own
 * agreements, owed by everyone. Not an app's, not a group's (`gatesAccount`).
 *
 * ── A FAILED CHECK OPENS THE ACCOUNT ─────────────────────────────────
 *
 * If the endpoint cannot be reached the account is shown as normal. Locking
 * somebody out of their own security settings because the agreements service
 * hiccuped would turn an outage there into an outage everywhere, and the next
 * load asks again.
 */
function useOwedAgreements(hint: unknown) {
  const ask = !(Array.isArray(hint) && hint.length === 0);
  const [state, setState] = useState<{
    status: 'checking' | 'done';
    pending: Agreement[];
    waitingForGuardian: Agreement[];
  }>({
    status: ask ? 'checking' : 'done',
    pending: [],
    waitingForGuardian: [],
  });
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!ask) {
      setState({ status: 'done', pending: [], waitingForGuardian: [] });
      return;
    }
    let live = true;
    getPendingAgreements()
      .then((r) => {
        const gated = (r.pending ?? []).filter(gatesAccount);
        /**
         * WAITING ON A GUARDIAN IS NOT SOMETHING TO ASK THIS PERSON FOR
         * (auth#615). Someone who needs a guardian's half and has given their
         * own sees the item still owed; the gate would ask them again for an
         * acceptance already on record and never clear. Those items open the
         * account and are reported in a banner instead.
         */
        const mine = gated.filter((a) => !(a.halves?.self === true && a.halves.guardian === false));
        const theirs = gated.filter((a) => a.halves?.self === true && a.halves.guardian === false);
        if (live) setState({ status: 'done', pending: mine, waitingForGuardian: theirs });
      })
      .catch((e: unknown) => {
        console.error('[account] could not check agreements', e);
        if (live) setState({ status: 'done', pending: [], waitingForGuardian: [] });
      });
    return () => {
      live = false;
    };
  }, [ask, round]);

  const recheck = useCallback(() => setRound((n) => n + 1), []);
  return { ...state, recheck };
}

/**
 * Whether this person is in a Family Group, for the tab and the guardian
 * banner. Null until known: a failed read hides the tab (the section is still
 * reachable from Connections and from the email) and leaves the banner on its
 * wording for either case.
 */
function useInFamily(ready: boolean): boolean | null {
  const [inFamily, setInFamily] = useState<boolean | null>(null);
  useEffect(() => {
    if (!ready) return;
    let live = true;
    getFamily()
      .then((f) => {
        if (live) setInFamily((f.groups ?? []).length > 0);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [ready]);
  return inFamily;
}

/**
 * Accepted, and waiting for a parent or guardian to accept too (auth#615).
 *
 * Someone in no Family Group yet is told how to get one, rather than about a
 * guardian "in your Family Group" they do not have (Jon, 2026-10-14). While
 * membership is unknown, the sentence covers both.
 */
function GuardianBanner({ agreements, inFamily }: { agreements: Agreement[]; inFamily: boolean | null }) {
  const names = agreements.map((a) => a.name).join(', ');
  const it = agreements.length === 1 ? 'it' : 'them';
  const who =
    inFamily === true
      ? `A parent or guardian in your Family Group needs to accept ${it} too.`
      : inFamily === false
        ? `A parent or guardian needs to accept ${it} too. Ask your parent or guardian to add you to their Family Group.`
        : `A parent or guardian needs to accept ${it} too. If you are not in a Family Group yet, ask your parent or guardian to add you to theirs.`;
  return (
    <div role="status" style={owedBanner}>
      <span>
        You have accepted the {names}. {who} Until then, anything in a Ridgewill app that needs a guardian stays
        locked.
      </span>
      <a href={FAMILY_PATH} style={{ color: C.brand, fontWeight: 600, whiteSpace: 'nowrap' }}>
        Family Group
      </a>
    </div>
  );
}

/** On Support while agreements are owed: the way back to them. */
function OwedBanner({ count }: { count: number }) {
  return (
    <div role="status" style={owedBanner}>
      <span>
        {count === 1 ? 'An updated agreement is' : 'Updated agreements are'} waiting for you. The rest of your account
        opens once you agree.
      </span>
      <a href="/" style={{ color: C.brand, fontWeight: 600, whiteSpace: 'nowrap' }}>
        Review {count === 1 ? 'it' : 'them'}
      </a>
    </div>
  );
}

const owedBanner: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '.5rem 1rem',
  flexWrap: 'wrap',
  margin: '0 0 1.25rem',
  padding: '.75rem 1rem',
  background: C.blueTint,
  color: C.blueText,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  fontSize: '.92rem',
  lineHeight: 1.45,
};

/**
 * Signed out, and sent straight to sign in.
 *
 * The screen this replaces was a page whose only content was a link to the page
 * you were trying to reach. It is still here, and it is what the SECOND arrival
 * renders — see lib/bounce.ts for why one automatic redirect is a convenience
 * and two in a row is a browser shuttling between origins forever.
 *
 * `replace`, not `assign`: the entry being navigated away from is one that would
 * immediately navigate again, so leaving it in history makes the Back button a
 * trap rather than a way out.
 *
 * The redirect lives in an effect rather than in render because it is a side
 * effect on the document, and React may render this twice — under StrictMode it
 * certainly does. Navigating from render would fire it during a render React is
 * free to throw away.
 */
/**
 * Long enough to read "You have been signed out" and believe it.
 *
 * Exported so the test waits exactly this long rather than guessing a number
 * that then has to be kept in step with this one.
 */
export const SIGNED_OUT_DELAY_MS = 2000;

function SignedOut() {
  const justSignedOut = signedOutDeliberately();
  const bounce = shouldBounce();

  useEffect(() => {
    /**
     * A DELIBERATE sign-out is carried on to sign-in, but not instantly.
     *
     * Instantly was the old objection to doing this at all, and it was a fair
     * one: shoving somebody straight back into a sign-in form undoes, silently,
     * the thing they just asked for, and can read as the sign-out having
     * failed. The pause is what answers it - the screen confirms the sign-out
     * worked, and only then moves.
     */
    if (justSignedOut) {
      const timer = setTimeout(() => {
        clearSignedOut();
        window.location.replace(signInUrl());
      }, SIGNED_OUT_DELAY_MS);
      return () => clearTimeout(timer);
    }
    if (!bounce) return;
    suppressBounce();
    window.location.replace(signInUrl());
  }, [justSignedOut, bounce]);

  if (justSignedOut) {
    return (
      <Shell title="Signed out" signIn>
        <p style={{ lineHeight: 1.5 }}>You have been signed out.</p>
        <p style={{ marginTop: '.5rem', color: C.textMid, fontSize: '.9rem', lineHeight: 1.5 }}>
          Taking you to the sign-in page.
        </p>
        <p style={{ marginTop: '1.5rem' }}>
          <a href={signInUrl()} style={{ color: C.brand }}>
            Sign in
          </a>
        </p>
      </Shell>
    );
  }

  if (bounce) {
    // No card: this paints for the length of one redirect, and a box that
    // appears and vanishes reads as a flicker rather than as a page.
    return <Shell title="Taking you to sign in…">{null}</Shell>;
  }

  return (
    <Shell title="Not signed in" signIn>
      {/*
        Two arrivals reach this, and the copy has to be true of both: somebody
        who has just pressed Sign out, and somebody the redirect sent to the
        login page and got back with no session. Naming only the second would
        report a fault to somebody who did this on purpose.
      */}
      <p style={{ lineHeight: 1.5 }}>You are signed out.</p>
      <p style={{ marginTop: '.5rem', color: C.textMid, fontSize: '.9rem', lineHeight: 1.5 }}>
        If you just tried to sign in and arrived back here, this site&rsquo;s cookies are most
        likely being blocked.
      </p>
      <p style={{ marginTop: '1.5rem' }}>
        <a href={signInUrl()} style={{ color: C.brand }}>
          Sign in
        </a>
      </p>
    </Shell>
  );
}

/**
 * The screen for "we could not establish who you are, and that is our fault".
 *
 * Extracted because there are now TWO ways to arrive: `get-session` erroring,
 * and `get-session` answering 200 with something that is not a session. They
 * are the same thing to the person reading the page and must not drift apart
 * in wording, which is what two copies of this markup would guarantee.
 *
 * The card is deliberate. Somebody who cannot get in should not also be looking
 * at a visibly broken page; see `Shell`.
 */
function ServerFault({ detail }: { detail?: string }) {
  return (
    <Shell title="Something went wrong">
      <p style={{ lineHeight: 1.5 }}>
        We couldn&rsquo;t check whether you&rsquo;re signed in. This is a problem on our end, not
        with your account.
      </p>
      <p style={{ marginTop: '1rem', color: C.textMid, fontSize: '.9rem', lineHeight: 1.5 }}>
        {detail || 'The server did not respond as expected.'}
      </p>
    </Shell>
  );
}

/** Renders nothing; re-arms the redirect for the next signed-out arrival in this
 *  tab. A component rather than a call in `App`, because `App` returns early
 *  several times before this point and a hook cannot live after those. */
function BounceReset() {
  useEffect(() => allowBounce(), []);
  return null;
}

/**
 * The chrome every state renders inside: ridgewill.com's own frame (Jon,
 * 2026-10-17, "First Light", RidgeWill/ridgewill-site#28).
 *
 * The site's top bar, then a band of the site's sky at the visitor's hour
 * carrying the page title, then the tabs, then a plain page and the site's
 * footer. The bar is transparent over the sky and turns cobalt once the sky
 * has scrolled away, which is also the moment the tabs reach it and stick
 * beneath it. Only the frame is the site's: every screen inside is unchanged.
 *
 * Signed out, errored and signed in all use it. A person who cannot sign in
 * should not be looking at a visibly worse page than one who can; that is the
 * state where doubting the site is most costly.
 *
 * The rules live in styles.css under "The frame", not inline like the rest of
 * this app, because hover, sticky offsets and the phone layout need selectors
 * and media queries.
 */
function Shell({
  title,
  children,
  banner,
  nav = false,
  family = false,
  here = '/',
  email,
  name,
  image,
  signIn = false,
}: {
  title: string;
  /** Show the Family Group tab: only once the person is in a group. */
  family?: boolean;
  children: React.ReactNode;
  /**
   * Rendered at the top of the page and OUTSIDE the screen (Jon, 2026-09-05).
   *
   * Not a child, and the distinction is the whole point of the prop. Passed as
   * a child it would read as part of whatever screen happens to be open: a
   * notice about the account wearing the frame of the Profile form. Out here
   * it belongs to the page rather than to the section, which is what an
   * interruption should look like.
   */
  banner?: React.ReactNode;
  nav?: boolean;
  here?: string;
  email?: string;
  name?: string | null;
  image?: string | null;
  /** Offer "Sign in" in the bar: only on the signed-out screens. */
  signIn?: boolean;
}) {
  // Once per load: a band that changed photograph while somebody was filling
  // in a form would be movement nobody asked for.
  const [sky] = useState(() => skyImageAt(new Date()));
  const stuck = useBarStuck();
  const who = email ? { email, first: firstName(name, email), image } : null;

  return (
    <>
      <header className={stuck ? 'rw-bar stuck' : 'rw-bar'}>
        <a className="rw-lockup" href={`${SITE_URL}/`} aria-label="Ridgewill home">
          <Logo />
          <b>Ridgewill</b>
          <span className="rw-section">Account</span>
        </a>
        <nav aria-label="Main">
          <a className="rw-link" href={`${SITE_URL}/#apps`}>
            Projects
          </a>
          <a className="rw-link" href={`${SITE_URL}/contact`}>
            Contact
          </a>
          <a className="rw-link" href={`${SITE_URL}/legal/`}>
            Legal
          </a>
          {who ? (
            <AccountMenu first={who.first} email={who.email} image={who.image} />
          ) : signIn ? (
            <a className="rw-pill" href={signInUrl()}>
              Sign in
            </a>
          ) : null}
        </nav>
      </header>

      <div className="rw-band" style={{ backgroundImage: `url(${sky})` }}>
        <div className="rw-frame rw-band-inner">
          {who ? (
            <div className="rw-me">
              <Avatar first={who.first} image={who.image} />
              <div>
                <h1>{title}</h1>
                <small>Signed in as {who.email}</small>
              </div>
            </div>
          ) : (
            <h1>{title}</h1>
          )}
        </div>
      </div>

      {nav && (
        <div className="rw-tabs">
          <nav aria-label="Account" className="rw-frame" ref={showCurrentTab}>
            {TABS.filter((t) => family || !('familyOnly' in t && t.familyOnly)).map((t) => (
              <NavLink key={t.path} href={t.path} label={t.label} active={isActive(here, t.path)} />
            ))}
          </nav>
        </div>
      )}

      <main className="rw-frame rw-page">
        {banner}

        {/*
          NOTHING WHEN THERE IS NOTHING IN IT.

          `isPending` renders `{null}`, and an empty wrapper that fades in and
          then resizes as content lands is a layout shift for nothing. The
          children are known at render, so not emitting it is simpler than
          hiding it afterwards.
        */}
        {children != null && <div className="rw-fade">{children}</div>}
      </main>

      <footer className="rw-foot">
        <div className="rw-frame">
          <span>© {new Date().getFullYear()} Ridgewill LLC · 4049 US Highway 231, PMB 1027, Wetumpka, AL 36093</span>
          <nav aria-label="Legal and contact">
            <a href={`${SITE_URL}/legal/`}>Legal information</a>
            <a href={`${SITE_URL}/legal/privacy/`}>Privacy</a>
            <a href={`${SITE_URL}/legal/terms/`}>Terms</a>
            <a href="/support">Support</a>
            <a href={`${SITE_URL}/contact`}>Contact</a>
          </nav>
          {/*
            On every state, signed in or not (account#87). Somebody who chose
            dark also sees "You are signed out" and the server-fault page, and
            both render before there is an account. Down here rather than in
            the bar because the bar sits on a photograph and this control is
            drawn for the page's own surface. Its list opens upwards when the
            room is above.
          */}
          <ThemeControl />
        </div>
      </footer>
    </>
  );
}

/**
 * On a phone the tabs scroll sideways, and Agreements or Support can start out
 * of sight. Bring the current one into the strip so the page says where it
 * is. Sideways only: the page itself must not move.
 */
function showCurrentTab(strip: HTMLElement | null) {
  const current = strip?.querySelector<HTMLElement>('a[aria-current="page"]');
  if (!strip || !current) return;
  const overflow = current.getBoundingClientRect().right - strip.getBoundingClientRect().right + 24;
  if (overflow > 0) strip.scrollLeft += overflow;
}

/** The bar's height; `--rw-bar-h` in styles.css. */
const BAR_HEIGHT = 64;

/**
 * Whether the sky has scrolled out from under the bar, so the bar takes its
 * cobalt and white links never sit on white. Read on resize too, since the
 * band's height follows the window width.
 */
function useBarStuck(): boolean {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const check = () => {
      const band = document.querySelector<HTMLElement>('.rw-band');
      setStuck(!band || window.scrollY > band.offsetHeight - BAR_HEIGHT);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);
  return stuck;
}

/** The name the bar greets: the first word of the name, else the email's local part. */
export function firstName(name: string | null | undefined, email: string): string {
  return (name ?? '').trim().split(/\s+/)[0] || email.split('@')[0] || email;
}

/**
 * The picture, over the initial. If the image fails the initial underneath is
 * what shows, rather than a broken-image glyph in a circle.
 */
function Avatar({ first, image }: { first: string; image?: string | null }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="rw-avatar" aria-hidden="true">
      <span>{first.charAt(0).toUpperCase()}</span>
      {image && !failed ? <img src={image} alt="" onError={() => setFailed(true)} /> : null}
    </span>
  );
}

/**
 * The avatar button and its menu: the site's, with the portal's own sign-out.
 * Closes on a click anywhere else and on Escape, which hands focus back to the
 * button so a keyboard user is not left nowhere.
 *
 * No "Account portal" row: on ridgewill.com it takes you here, and here it
 * would only reload the page you are on (Jon, 2026-10-21). Support stays,
 * because it goes to a section.
 */
function AccountMenu({
  first,
  email,
  image,
}: {
  first: string;
  email: string;
  image?: string | null;
}) {
  const [open, setOpen] = useState(false);
  /** Every account this browser holds; null until known, or when it cannot be. */
  const [held, setHeld] = useState<DeviceAccount[] | null>(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let live = true;
    void listDeviceAccounts().then((list) => {
      if (live) setHeld(list);
    });
    return () => {
      live = false;
    };
  }, [email]);

  // By email rather than id: the bar is handed the email, and an address
  // belongs to one account.
  const others = (held ?? []).filter((a) => a.user.email !== email);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="rw-acct" ref={wrap}>
      <button
        ref={button}
        type="button"
        className="rw-acct-btn"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar first={first} image={image} />
        <span>{first}</span>
      </button>
      {open && (
        <div className="rw-acct-menu">
          <p>
            <strong>Signed in as {first}</strong>
            {email}
          </p>
          {held && (others.length > 0 || held.length < MAX_ACCOUNTS) && (
            <div className="rw-switch">
              {others.length > 0 && <span className="rw-switch-label">Switch to</span>}
              {others.map((a) => (
                <button
                  key={a.session.token}
                  type="button"
                  className="rw-switch-row"
                  disabled={switching}
                  onClick={() => {
                    setSwitching(true);
                    void switchTo(a.session.token).then(() => window.location.reload());
                  }}
                >
                  <Avatar first={a.user.name || a.user.email} image={a.user.image} />
                  <span className="rw-switch-who">
                    <b>{a.user.name || a.user.email}</b>
                    {a.user.name && <small>{a.user.email}</small>}
                  </span>
                </button>
              ))}
              {held.length < MAX_ACCOUNTS && (
                <a className="rw-switch-add" href={signInUrl()}>
                  Add another account
                </a>
              )}
            </div>
          )}
          <a href="/support">Support</a>
          <button type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** The Ridgewill ridge. Turquoise front peak, white rear: the site's lockup on
 *  its sky and its cobalt bar. The one place a colour is hardcoded, because the
 *  mark is the mark. Inline rather than imported: one glyph does not justify an
 *  icon module. */
function Logo({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M3 26 L11 11 L19 26 Z" fill="#40E0D0" />
      <path d="M13 26 L22 8 L31 26 Z" fill="#fff" />
    </svg>
  );
}

/**
 * Sign in, and COME BACK HERE.
 *
 * ── Without `returnTo` this is a one-way trip ────────────────────────
 *
 * Which is what shipped: the link was `${LOGIN_URL}/login`, so a successful
 * sign-in landed on the auth service's placeholder — a page that says there is
 * nowhere to send you — and left somebody signed in, on a different site,
 * looking at a screen that reads as the portal having refused them. auth#59 is
 * the same bug in org-admin, fixed there and then reintroduced here by a link
 * that looked complete.
 *
 * `href` and not `origin`, so a deep link survives: being signed out while
 * opening /security should return you to /security, which is exactly the link
 * somebody follows after losing their phone.
 *
 * ── Passing a whole URL is safe because THIS is not what navigates ───
 *
 * The auth service decides. `/api/return` checks the candidate's origin against
 * its own `TRUSTED_ORIGINS` and falls back to `/` when it does not recognise
 * it — so a crafted `?returnTo=https://evil.test` reaches a check that refuses
 * it, and no screen here can reintroduce an open redirector by forgetting to
 * validate. The corollary is operational, and it is silent: this origin must be
 * in the HOT PATH's trusted list, not merely the account deployment's. If it is
 * not, sign-in still succeeds and still lands on the placeholder, looking
 * exactly like the bug this fixes.
 *
 * `LOGIN_URL`, not the account API: that deployment refuses `/sign-in/email` on
 * purpose, so a link there is a form that cannot submit.
 *
 * ── THE SLUG IS THIS APP'S REGISTERED NAME ───────────────────────────
 *
 * RidgeWill/auth#86 made `returnTo` accept an app name, so the sign-in page
 * shows `?returnTo=account&at=/security` rather than forty characters of
 * percent-encoding. That page is the front door for end users now, and an
 * address bar full of encoded URL is what a phishing page looks like.
 *
 * `account` is our `Application.slug` — registered in the admin portal, marked
 * PERMANENT there because it is already stamped into token audiences and
 * `Agreement.appSlug`. The auth service resolves it through a matching name in
 * its own TRUSTED_ORIGINS.
 *
 * A CONSTANT, not `import.meta.env`. The slug is the same in every environment
 * by definition — it names the application, not the deployment — so a build-time
 * variable could only ever introduce a way to be wrong, and this repo has
 * already paid for that twice with an unset VITE_ACCOUNT_URL and a missing
 * returnTo. Deriving it from the hostname was the other tempting option and is
 * worse: it invents a second name for something the platform already names, and
 * the two disagree for the admin portal (`admin` derived, `org-admin`
 * registered).
 */
const APP_SLUG = 'account';

export function signInUrl(here: string = window.location.href): string {
  return `${LOGIN_URL}/login?${returnQuery(here)}`;
}

/** `returnTo=account&at=<path>`: back to this page, by this app's name. */
export function returnQuery(here: string = window.location.href): string {
  const url = new URL(here);
  // `pathname + search + hash`, so a deep link survives: signed out while
  // opening /security should return you to /security, which is the link somebody
  // follows after losing their phone. A slug alone lands everyone on the root.
  const at = `${url.pathname}${url.search}${url.hash}`;
  return `returnTo=${APP_SLUG}&at=${encodeURIComponent(at)}`;
}

/**
 * WHICH OF THE FOUR STATES THE PORTAL IS IN, as a pure decision.
 *
 * ── A truthy session is not the same as a usable one (#16) ───────────
 *
 * This used to be `if (!session) return <SignedOut />` followed immediately by
 * `session.user as {...}`. A session object carrying no `user` therefore passed
 * the guard and threw on the first dereference, and a throw during render means
 * React unmounts the tree: no error screen, no sign-in link, a WHITE PAGE.
 *
 * That is reachable without anything being wrong with the account. Run the
 * portal with `VITE_AUTH_URL` unset and `BASE` becomes `''`, so the client asks
 * its OWN origin for a session, the SPA fallback answers 200 with index.html,
 * and the client hands back a session-shaped object with no user and no error.
 * The `if (error)` branch, written precisely so a server fault is not mistaken
 * for a sign-out, never runs, because there is no error.
 *
 * It is the third time this org has met a 200 with an HTML body read as data:
 * auth#112 swallowed it into "no such user", auth#150 was the `/me` router
 * falling through to the SPA. This is the browser end of the same shape, so it
 * is classed with the server faults and not with being signed out.
 *
 * Exported and pure for the same reason `isActive` and `normalisePath` are: it
 * is a decision about a value, and it deserves a test rather than somebody
 * remembering to unset an environment variable and look at the page.
 */
export type SessionState = 'loading' | 'signed-out' | 'fault' | 'ready';

export function sessionState({
  isPending,
  error,
  session,
}: {
  isPending: boolean;
  error?: unknown;
  session?: { user?: unknown } | null;
}): SessionState {
  if (isPending) return 'loading';
  // Before the null check, because a failed lookup also leaves `session` null
  // and collapsing the two reproduces auth#32/#33 exactly.
  if (error) return 'fault';
  if (!session) return 'signed-out';
  // Truthy session, no user. NOT signed out - nobody asked to be, and saying so
  // would send them to a login page that will hand back the same broken answer.
  return session.user ? 'ready' : 'fault';
}

/** `/` must not light up for every path — it is a prefix of all of them. */
/**
 * `/sessions` and `/passkeys` were tabs of their own and are now sections of
 * `/security`.
 *
 * Mapped rather than dropped, because a bookmark or a link from another Ridgewill
 * app would otherwise land on Profile — a 200 showing the wrong page, which is
 * the worst of the three possible outcomes. A 404 would at least say something;
 * silently rendering a different screen says nothing and looks deliberate.
 *
 * Exported for the same reason `isActive` is: it is a pure decision about a
 * string and deserves a test rather than a manual click.
 */
export function normalisePath(path: string): string {
  return path === '/sessions' || path === '/passkeys' ? '/security' : path;
}

/**
 * A tab also owns the pages under it, so `/connections/<id>` keeps Connections
 * lit. `/` is excluded from that for the reason above.
 */
export function isActive(here: string, tab: string): boolean {
  return tab === '/' ? here === '/' || here === '' : here === tab || here.startsWith(`${tab}/`);
}

function NavLink({ href, label, active }: { href: string; label: string; active: boolean }) {
  return (
    <a href={href} aria-current={active ? 'page' : undefined}>
      {label}
    </a>
  );
}
